import { useState, useRef, useEffect, useCallback } from 'react'
import { RouteData, POI, Photo, useAppStore } from '@cya-routes/shared'
import { CompactPOIModal } from './CompactPOIModal'
import { CompactPhotoModal } from './CompactPhotoModal'
import { ElevationProfileSection } from './ElevationProfileSection'
import { RouteEmbedModal } from '../embed/RouteEmbedModal'
import { MAP_LAYERS } from '../map/MapLayerControl'
import { MapControlGroup } from '../map/MapControlGroup'
import { SidebarDrawer } from '../editor/SidebarDrawer'
import { RouteNavigator } from './RouteNavigator'
import { POIFilterPanel } from './POIFilterPanel'
import { useMasterRoute } from '../../hooks/useMasterRoute'
import { useUserPOISystem } from '../../hooks/useUserPOISystem'
import { useMapLifecycle } from './hooks/useMapLifecycle'
import { useTracerMarker } from './hooks/useTracerMarker'
import { useRouteSelection } from './hooks/useRouteSelection'
import { usePhotoManagement } from './hooks/usePhotoManagement'
import { usePOIManagement } from './hooks/usePOIManagement'
import { useFlybyControls } from './hooks/useFlybyControls'
import { useMapRenderers } from './hooks/useMapRenderers'
import { calculateBounds, getCoordinatesAtDistance } from '../../utils/routeDistanceHelpers'
import { POI_CATEGORIES } from '../../data/poiTypes'
import { 
  Route, 
  Calendar,
  User,
  Building2,
  Camera,
  CameraOff,
  ArrowLeft,
  MapPin,
  MapPinOff,
  Play,
  Pause,
  Square,
  Code,
  RotateCw,
  RotateCcw,
  MoveVertical,
  ZoomIn,
  ZoomOut
} from 'lucide-react'

interface RouteDisplayPageProps {
  routes: RouteData[]
  pois?: POI[]
  photos?: Photo[]
  routeInfo: {
    name: string
    description: string
    routeType: string
    creator: string
    createdAt?: Date
    club?: {
      name: string
      slug: string
    }
    logoUrl?: string
    secondaryLogoUrl?: string
    hasMasterRoute?: boolean
  }
  isPublic?: boolean
  showHeader?: boolean
  className?: string
  onClose?: () => void
  routeId?: string // Add optional routeId prop
}




export function RouteDisplayPage({ 
  routes, 
  pois = [], 
  photos = [], 
  routeInfo, 
  isPublic = true, // eslint-disable-line @typescript-eslint/no-unused-vars
  showHeader = true,
  className = '',
  onClose,
  routeId
}: RouteDisplayPageProps) {
  const [currentLayer, setCurrentLayer] = useState('satellite')
  const [showElevation, setShowElevation] = useState(true)
  // Default sidebar state - open route drawer on desktop only
  const [activeSidebarDrawer, setActiveSidebarDrawer] = useState<string | null>(window.innerWidth >= 768 ? 'route' : null)
  const [showDetailedStats, setShowDetailedStats] = useState<{ [key: string]: boolean }>({}) // Track detailed stats per route
  const [is3DEnabled, setIs3DEnabled] = useState(true) // Track 3D terrain state
  const [showEmbedModal, setShowEmbedModal] = useState(false)
  const [hoveredClimb, setHoveredClimb] = useState<{ index: number, climb: any } | null>(null)
  const [elevationProfileMinimized, setElevationProfileMinimized] = useState(false)
  const [elevationActiveTab, setElevationActiveTab] = useState('elevation')
  
  // Refs for smooth tour controls
  const bearingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const pitchIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const zoomIntervalRef = useRef<NodeJS.Timeout | null>(null)
  
  // Calculate elevation profile height
  const getElevationProfileHeight = () => {
    if (!showElevation) return 0
    const isMobile = window.innerWidth < 768
    if (elevationProfileMinimized) return isMobile ? 65 : 40
    if (elevationActiveTab === 'segment' || elevationActiveTab === 'discussion') return isMobile ? 450 : 550
    return isMobile ? 275 : 233 // Original mobile height
  }
  
  // Smooth control functions for tour
  const startBearingAdjustment = useCallback((delta: number) => {
    if (!flybyRef.current) return
    flybyRef.current.adjustBearing(delta)
    bearingIntervalRef.current = setInterval(() => {
      flybyRef.current?.adjustBearing(delta)
    }, 100)
  }, [])
  
  const stopBearingAdjustment = useCallback(() => {
    if (bearingIntervalRef.current) {
      clearInterval(bearingIntervalRef.current)
      bearingIntervalRef.current = null
    }
  }, [])
  
  const startPitchAdjustment = useCallback((delta: number) => {
    if (!flybyRef.current) return
    flybyRef.current.adjustPitch(delta)
    pitchIntervalRef.current = setInterval(() => {
      flybyRef.current?.adjustPitch(delta)
    }, 100)
  }, [])
  
  const stopPitchAdjustment = useCallback(() => {
    if (pitchIntervalRef.current) {
      clearInterval(pitchIntervalRef.current)
      pitchIntervalRef.current = null
    }
  }, [])
  
  const startZoomAdjustment = useCallback((delta: number) => {
    if (!flybyRef.current) return
    flybyRef.current.adjustZoom(delta)
    zoomIntervalRef.current = setInterval(() => {
      flybyRef.current?.adjustZoom(delta)
    }, 100)
  }, [])
  
  const stopZoomAdjustment = useCallback(() => {
    if (zoomIntervalRef.current) {
      clearInterval(zoomIntervalRef.current)
      zoomIntervalRef.current = null
    }
  }, [])
  
  // Use hasMasterRoute from routeInfo if provided, otherwise default to false
  const [hasMasterRoute] = useState(routeInfo.hasMasterRoute ?? false)
  
  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      stopBearingAdjustment()
      stopPitchAdjustment()
      stopZoomAdjustment()
    }
  }, [stopBearingAdjustment, stopPitchAdjustment, stopZoomAdjustment])
  



  // Create refs for the callbacks that will be updated later
  const handlePOISelectRef = useRef<(poi: POI) => void>(() => {})
  const handlePhotoSelectRef = useRef<(photo: Photo) => void>(() => {})
  
  // Wrapper functions that call the refs
  const handlePOISelect = useCallback((poi: POI) => {
    handlePOISelectRef.current(poi)
  }, [])
  
  const handlePhotoSelect = useCallback((photo: Photo) => {
    handlePhotoSelectRef.current(photo)
  }, [])

  // Initialize map and renderers (without userPOI callback for now)
  const {
    mapContainer,
    mapManager,
    routeRenderer,
    poiRenderer,
    photoRenderer,
    commentRenderer,
    userPoiRenderer,
    renderersInitialized
  } = useMapLifecycle({
    onPOISelect: handlePOISelect,
    onPhotoSelect: handlePhotoSelect,
    is3DEnabled
  })
  
  // Photo management - moved before callback effect
  const {
    selectedPhoto,
    setSelectedPhoto,
    showPhotoModal,
    setShowPhotoModal,
    showPhotos,
    setShowPhotos,
    sortedPhotos,
    handlePhotoSelect: photoSelectHandler,
    handlePhotoNavigate: photoNavigateHandler
  } = usePhotoManagement({
    photos,
    routes
  })
  
  // Wrap handlePhotoNavigate to include map panning
  const handlePhotoNavigate = useCallback((direction: 'prev' | 'next') => {
    // First navigate to the new photo
    photoNavigateHandler(direction)
    
    // Then trigger the map panning for the new photo
    // We need to do this in a setTimeout to ensure the state has updated
    setTimeout(() => {
      const currentIndex = sortedPhotos.findIndex(p => p.id === selectedPhoto?.id)
      let newIndex: number
      
      if (direction === 'prev') {
        newIndex = currentIndex > 0 ? currentIndex - 1 : sortedPhotos.length - 1
      } else {
        newIndex = currentIndex < sortedPhotos.length - 1 ? currentIndex + 1 : 0
      }
      
      const newPhoto = sortedPhotos[newIndex]
      if (newPhoto) {
        // Trigger the same panning behavior as photo selection
        handlePhotoSelectRef.current(newPhoto)
      }
    }, 0)
  }, [selectedPhoto, sortedPhotos, photoNavigateHandler])
  
  // POI management
  const {
    selectedPOI,
    setSelectedPOI,
    showPOIModal,
    setShowPOIModal,
    showPOIs,
    setShowPOIs,
    hiddenPOICategories,
    setHiddenPOICategories,
    visiblePOIs,
    handlePOISelect: poiSelectHandler
  } = usePOIManagement({
    pois
  })
  
  // Update callback implementations now that we have the refs
  useEffect(() => {
    handlePOISelectRef.current = (poi: POI) => {
      // Use the POI management hook handler
      poiSelectHandler(poi)
      
      // Center map on the POI with a slight offset to account for the modal
      if (mapManager.current && poi.coordinates) {
        const map = mapManager.current.getMap()
        if (map) {
          // Calculate offset to shift POI slightly left to account for right-side modal
          // Modal is 384px wide, so we offset by about half that in map coordinates
          const currentZoom = map.getZoom()
          const offsetX = -150 // Negative to move left
          
          map.easeTo({
            center: [poi.coordinates.lng, poi.coordinates.lat],
            offset: [offsetX, 0],
            duration: 500,
            essential: true
          })
        }
      }
    }
    
    handlePhotoSelectRef.current = (photo: Photo) => {
      // Use the photo management hook handler
      photoSelectHandler(photo)
      
      // Highlight the selected photo on the map
      if (photoRenderer.current) {
        photoRenderer.current.highlightPhoto(photo.id)
      }
      
      // Create immersive 3D view with photo on left-center
      if (mapManager.current && photo.coordinates) {
        const map = mapManager.current.getMap()
        if (map) {
          // Calculate offset to position photo in left-center
          // Modal is 422px wide on right, so we need to offset left more
          const offsetX = -300 // Left offset to account for modal
          const offsetY = 0 // No vertical offset needed
          
          // Get current zoom and ensure minimum of 12
          const currentZoom = map.getZoom()
          const targetZoom = Math.max(currentZoom, 12)
          
          map.easeTo({
            center: [photo.coordinates.lng, photo.coordinates.lat],
            offset: [offsetX, offsetY],
            pitch: 70, // Set pitch to 70 as requested
            zoom: targetZoom, // Zoom to at least 12, but don't zoom out if already closer
            // Don't change bearing
            duration: 800,
            essential: true
          })
        }
      }
    }
  }, [mapManager, photoRenderer, photoSelectHandler, poiSelectHandler])

  // Get user authentication for comments
  const { auth } = useAppStore()
  const user = auth.currentUser

  // Route selection management
  const { selectedRouteId, setSelectedRouteId, zoomToRoute } = useRouteSelection({
    routes,
    mapManager
  })

  // Master route management for multi-route displays
  const {
    getCurrentRoute,
    getNavigableRoutes,
    navigateToRoute,
    masterRoute
  } = useMasterRoute({
    routes,
    selectedRouteId,
    hasMasterRoute,
    setSelectedRouteId,
    zoomToRoute
  })
  
  // Use the tracer marker hook
  const currentRoute = getCurrentRoute()
  const { tracerPosition, updateTracerPosition } = useTracerMarker({
    mapManager,
    currentRoute,
    renderersInitialized
  })
  
  // Auto-select first route or master route
  useEffect(() => {
    if (!selectedRouteId && routes.length > 0) {
      // If there's a master route and multiple segments, default to master
      if (masterRoute && routes.length > 1) {
        setSelectedRouteId('master-route')
      } else {
        // Otherwise select the first route
        setSelectedRouteId(routes[0].id)
      }
    }
  }, [selectedRouteId, routes, masterRoute, setSelectedRouteId])
  
  // Handle POI mention clicks
  useEffect(() => {
    const handlePOIMentionClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      
      // Check if clicked element is a POI mention or a child of one
      const poiMention = target.closest('.poi-mention') as HTMLElement
      
      if (poiMention && poiMention.hasAttribute('data-poi-id')) {
        e.preventDefault()
        e.stopPropagation()
        
        const poiId = poiMention.getAttribute('data-poi-id')
        if (poiId) {
          const poi = pois.find(p => p.id === poiId)
          if (poi) {
            console.log('POI mention clicked:', poi.name)
            handlePOISelect(poi) // This will open the POI modal
          }
        }
      }
    }
    
    // Add event listener
    document.addEventListener('click', handlePOIMentionClick)
    
    // Cleanup
    return () => document.removeEventListener('click', handlePOIMentionClick)
  }, [pois, handlePOISelect])
  
  // Flyby controls
  const {
    flybyState,
    flybyProgress,
    flybySpeed,
    startFlyby,
    pauseFlyby,
    resumeFlyby,
    stopFlyby,
    changeSpeed,
    flybyRef
  } = useFlybyControls({
    mapManager,
    getCurrentRoute,
    selectedRouteId,
    zoomToRoute,
    is3DEnabled,
    setIs3DEnabled,
    routeRenderer,
    poiRenderer
  })

  // Use map renderers hook to handle all rendering
  useMapRenderers({
    routes,
    selectedRouteId,
    masterRoute,
    visiblePOIs,
    sortedPhotos,
    showPhotos,
    selectedPhoto,
    renderersInitialized,
    routeRenderer,
    poiRenderer,
    photoRenderer,
    commentRenderer
  })

  // Handle climb hover highlighting
  useEffect(() => {
    if (routeRenderer.current && renderersInitialized) {
      if (hoveredClimb && selectedRouteId) {
        // Find the currently selected route
        const selectedRoute = selectedRouteId === 'master-route' ? masterRoute : routes.find(r => r.id === selectedRouteId)
        if (selectedRoute) {
          // Highlight the climb segment with the climb's color
          routeRenderer.current.highlightRouteSegment(
            selectedRouteId,
            hoveredClimb.climb.startDistance,
            hoveredClimb.climb.endDistance,
            selectedRoute,
            hoveredClimb.climb.color
          )
        }
      } else {
        // Clear highlight
        routeRenderer.current.highlightRouteSegment(null, null, null, null)
      }
    }
  }, [hoveredClimb, selectedRouteId, routes, masterRoute, renderersInitialized, routeRenderer])

  // Fit map to routes
  useEffect(() => {
    if (routes.length > 0 && mapManager.current) {
      const allCoordinates = routes.flatMap(route => route.coordinates || [])
      if (allCoordinates.length > 0) {
        const bounds = calculateBounds(allCoordinates)
        mapManager.current.fitBounds(bounds)
      }
    }
  }, [routes, mapManager])

  // Handle hover distance changes from elevation profile
  const handleHoverDistanceChange = useCallback((distance: number | null) => {
    
    if (distance === null) {
      updateTracerPosition(null)
      return
    }

    const currentRoute = getCurrentRoute()
    if (!currentRoute) {
      updateTracerPosition(null)
      return
    }

    // Handle master route case
    if (selectedRouteId === 'master-route' && routes.length > 1) {
      // Find which segment contains this distance
      let cumulativeDistance = 0
      for (const route of routes) {
        const routeDistance = route.stats?.distance || 0
        if (distance <= cumulativeDistance + routeDistance) {
          // This is the segment containing the hover position
          const segmentDistance = distance - cumulativeDistance
          const coordinates = getCoordinatesAtDistance(route, segmentDistance)
          if (coordinates) {
            updateTracerPosition({ distance, coordinates })
          }
          return
        }
        cumulativeDistance += routeDistance
      }
    } else {
      // Single route case
      const coordinates = getCoordinatesAtDistance(currentRoute, distance)
      if (coordinates) {
        updateTracerPosition({ distance, coordinates })
      }
    }
  }, [selectedRouteId, routes, getCurrentRoute, updateTracerPosition])

  // Handle climb hover from elevation profile
  const handleClimbHover = useCallback((climbInfo: { index: number, climb: any } | null) => {
    setHoveredClimb(climbInfo)
  }, [])

  const handleLayerChange = useCallback((layerId: string) => {
    const layer = MAP_LAYERS.find(l => l.id === layerId)
    if (layer && mapManager.current) {
      setCurrentLayer(layerId)
      const mapInstance = mapManager.current.getMap()
      
      if (!mapInstance) {
        console.error('No map instance available')
        return
      }
      
      // Handle Google layers differently
      if (layer.style.startsWith('google-')) {
        // Use a simple Mapbox style and replace with Google tiles
        mapManager.current.setStyle({
          version: 8,
          sources: {
            'google': {
              type: 'raster',
              tiles: layer.style === 'google-satellite' 
                ? ['https://mt0.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
                   'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
                   'https://mt2.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
                   'https://mt3.google.com/vt/lyrs=s&x={x}&y={y}&z={z}']
                : layer.style === 'google-hybrid'
                ? ['https://mt0.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
                   'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
                   'https://mt2.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
                   'https://mt3.google.com/vt/lyrs=y&x={x}&y={y}&z={z}']
                : ['https://mt0.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
                   'https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
                   'https://mt2.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
                   'https://mt3.google.com/vt/lyrs=p&x={x}&y={y}&z={z}'],
              tileSize: 256,
              attribution: '© Google Maps'
            }
          },
          layers: [{
            id: 'google',
            type: 'raster',
            source: 'google',
            paint: {}
          }],
          glyphs: "mapbox://fonts/mapbox/{fontstack}/{range}.pbf"
        })
        
        // Wait for style to load and re-render content
        mapInstance.once('style.load', () => {
          console.log('✅ Google layer style loaded:', layer.style)
          // Re-render all content after style loads
          if (routeRenderer.current) {
            routeRenderer.current.renderRoutes(routes, selectedRouteId || undefined, masterRoute || undefined)
          }
          if (poiRenderer.current) {
            poiRenderer.current.renderPOIs(visiblePOIs)
          }
          if (photoRenderer.current) {
            photoRenderer.current.renderPhotos(showPhotos ? sortedPhotos : [], selectedPhoto?.id)
          }
          
          // Re-add terrain
          try {
            if (!mapInstance.getSource('mapbox-dem')) {
              mapInstance.addSource('mapbox-dem', {
                type: 'raster-dem',
                url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
                tileSize: 512,
                maxzoom: 14
              })
            }
            if (is3DEnabled) {
              mapInstance.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 })
              mapInstance.setPitch(30)
            }
          } catch (error) {
            console.log('3D terrain not available')
          }
        })
        
      } else {
        // For regular Mapbox styles
        mapManager.current.setStyle(layer.style)
        
        mapInstance.once('style.load', () => {
          console.log('🎨 Mapbox style loaded:', layer.id)
          // Re-render all content after style loads
          if (routeRenderer.current) {
            routeRenderer.current.renderRoutes(routes, selectedRouteId || undefined, masterRoute || undefined)
          }
          if (poiRenderer.current) {
            poiRenderer.current.renderPOIs(visiblePOIs)
          }
          if (photoRenderer.current) {
            photoRenderer.current.renderPhotos(showPhotos ? sortedPhotos : [], selectedPhoto?.id)
          }
          
          // Re-add terrain
          try {
            if (!mapInstance.getSource('mapbox-dem')) {
              mapInstance.addSource('mapbox-dem', {
                type: 'raster-dem',
                url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
                tileSize: 512,
                maxzoom: 14
              })
            }
            if (is3DEnabled) {
              mapInstance.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 })
              mapInstance.setPitch(30)
            }
          } catch (error) {
            console.log('3D terrain not available')
          }
        })
      }
    }
  }, [routes, selectedRouteId, masterRoute, visiblePOIs, showPhotos, sortedPhotos, selectedPhoto?.id, is3DEnabled])



  // Initialize user POI system (only if we have a route ID)
  const userPOIRouteId = routes[0]?.id || 'default'
  const userPOISystem = useUserPOISystem({
    routeId: userPOIRouteId,
    route: currentRoute,
    userId: user?.id,
    userDisplayName: user ? `${user.firstName} ${user.lastName}`.trim() : 'Anonymous',
    userPhotoURL: user?.profilePhoto || undefined,
    mapManager,
    userPoiRenderer,
    renderersInitialized
  })
  
  // Create state for controlling user POI visibility from elevation profile
  const [showUserPOIsInProfile, setShowUserPOIsInProfile] = useState(true)
  

  // Sidebar items for display mode - route navigation and photo toggle
  const sidebarItems = [
    {
      id: 'route',
      icon: <Route className="w-5 h-5" />,
      label: 'Route',
      content: (
        <RouteNavigator
          routes={routes}
          masterRoute={masterRoute}
          selectedRouteId={selectedRouteId}
          showDetailedStats={showDetailedStats}
          showElevation={showElevation}
          hasCurrentRoute={!!getCurrentRoute()}
          onRouteSelect={(routeId) => {
            setSelectedRouteId(routeId)
            zoomToRoute(routeId)
          }}
          onShowDetailedStatsToggle={(routeId) => {
            setShowDetailedStats(prev => ({
              ...prev,
              [routeId]: !prev[routeId]
            }))
          }}
          onShowElevation={() => setShowElevation(true)}
        />
      )
    },
    // Photo toggle
    {
      id: 'photo-toggle',
      icon: showPhotos ? <Camera className="w-5 h-5" /> : <CameraOff className="w-5 h-5" />,
      label: showPhotos ? 'Photos' : 'Photos Off',
      isDirectAction: true,
      onClick: () => setShowPhotos(!showPhotos)
    },
    // POI toggle
    {
      id: 'poi-toggle',
      icon: showPOIs ? <MapPin className="w-5 h-5" /> : <MapPinOff className="w-5 h-5" />,
      label: showPOIs ? 'POIs' : 'POIs Off',
      content: showPOIs && (
        <POIFilterPanel
          pois={pois}
          hiddenPOICategories={hiddenPOICategories}
          onToggleCategory={(categoryId) => {
            const newHidden = new Set(hiddenPOICategories)
            if (newHidden.has(categoryId)) {
              newHidden.delete(categoryId)
            } else {
              newHidden.add(categoryId)
            }
            setHiddenPOICategories(newHidden)
          }}
          onShowAllCategories={() => setHiddenPOICategories(new Set())}
          onHideAllCategories={() => setHiddenPOICategories(new Set(POI_CATEGORIES.map(c => c.id)))}
        />
      ),
      onClick: () => {
        if (!showPOIs) {
          // When turning POIs back on, show all categories
          setHiddenPOICategories(new Set())
        }
        setShowPOIs(!showPOIs)
      }
    },
    // User POIs (Reports)
    ...(userPOISystem ? [userPOISystem.sidebarItem] : []),
    // Embed button
    {
      id: 'embed',
      icon: <Code className="w-5 h-5" />,
      label: 'Embed',
      isDirectAction: true,
      onClick: () => setShowEmbedModal(true)
    },
    // Image Generator - Hidden for now due to export sizing issues
    // {
    //   id: 'image-generator',
    //   icon: <Image className="w-6 h-6" />,
    //   label: 'Generate Image',
    //   isDirectAction: true,
    //   onClick: () => setShowImageGenerator(true)
    // }
  ]

  return (
    <div className={`route-display-page flex flex-col h-screen overflow-hidden ${className}`} style={{ height: '100dvh' }}>
      {/* Header */}
      {showHeader && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gray-800 border-b border-gray-700 p-3 sm:p-2" style={{ paddingLeft: 'max(12px, env(safe-area-inset-left))', paddingRight: 'max(12px, env(safe-area-inset-right))' }}>
          <div className="relative">
            {/* Back Button - Absolute positioned on left */}
            {onClose && (
              <button
                onClick={onClose}
                className="absolute left-0 top-0 flex items-center gap-1.5 px-2 py-1 hover:bg-emerald-700/20 rounded-lg transition-colors group"
              >
                <ArrowLeft className="w-3.5 h-3.5 text-emerald-400 group-hover:text-emerald-300" />
                <span className="text-xs text-emerald-400 group-hover:text-emerald-300">Back</span>
              </button>
            )}
            
            {/* Centered Content */}
            <div className="text-center">
              <div className="flex items-center justify-center gap-3 mb-1">
                {/* Logo */}
                {routeInfo.logoUrl && (
                  <img 
                    src={routeInfo.logoUrl} 
                    alt="Route Logo" 
                    className="h-8 w-auto object-contain"
                    style={{ backgroundColor: 'transparent' }}
                  />
                )}
                
                {/* Route Name */}
                <h1 className="text-lg font-bold text-white">{routeInfo.name}</h1>
                
                {/* Secondary Logo */}
                {routeInfo.secondaryLogoUrl && (
                  <img 
                    src={routeInfo.secondaryLogoUrl} 
                    alt="Secondary Logo" 
                    className="h-8 w-auto object-contain"
                    style={{ backgroundColor: 'transparent' }}
                  />
                )}
              </div>
              <div className="flex items-center justify-center gap-3 mt-1 text-xs text-gray-400">
                <div className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  <span>by {routeInfo.creator}</span>
                </div>
                {routeInfo.club && (
                  <div className="flex items-center gap-1">
                    <Building2 className="w-3 h-3" />
                    <span>{routeInfo.club.name}</span>
                  </div>
                )}
                {routeInfo.createdAt && (
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>{routeInfo.createdAt.toLocaleDateString()}</span>
                  </div>
                )}
                <span className="px-1.5 py-0.5 bg-emerald-700 text-emerald-300 rounded text-[10px] font-medium">
                  {routeInfo.routeType.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Full Screen Map Layout */}
      <div className={`flex-1 relative overflow-hidden ${showHeader ? 'pt-16' : ''}`}>
        {/* Full Screen Map - positioned absolute to fill entire container */}
        <div className="absolute inset-0 route-display-map">
          <div ref={mapContainer} className="w-full h-full" />
        </div>

        {/* Map Controls - positioned over map */}
        <div className="absolute z-30" style={{ top: '200px', right: '10px' }}>
          <MapControlGroup 
            map={mapManager.current?.getMap() || null}
            currentLayer={currentLayer}
            onLayerChange={handleLayerChange}
            is3DEnabled={is3DEnabled}
            onToggle3D={setIs3DEnabled}
          />
        </div>

        {/* Flyby Controls - Bottom Right */}
        <div 
          className="absolute z-30 transition-all duration-300" 
          style={{ 
            bottom: `${getElevationProfileHeight() + (window.innerWidth < 768 ? 10 : 20)}px`, 
            right: '10px' 
          }}
        >
                {flybyState === 'stopped' ? (
                  <button
                    onClick={startFlyby}
                    className="flex items-center justify-center px-4 py-2 bg-emerald-600/80 hover:bg-emerald-700/80 text-white rounded-lg transition-colors text-sm font-medium shadow-lg backdrop-blur-sm"
                    title="Start 3D Route Flyby"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    3D Tour
                  </button>
                ) : (
                  <div className="bg-gray-800/70 backdrop-blur-sm rounded-xl shadow-xl border border-white/10">
                    {/* Media Player with Liquid Glass Styling */}
                    <div className="p-3">
                      {/* First Row - Playback & Progress */}
                      <div className="flex items-center gap-3 mb-3">
                        {/* Playback Controls */}
                        <div className="flex items-center gap-1">
                          {flybyState === 'playing' ? (
                            <button
                              onClick={pauseFlyby}
                              className="p-2 hover:bg-blue-500/20 text-white rounded-lg transition-all duration-200"
                              title="Pause"
                            >
                              <Pause className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={resumeFlyby}
                              className="p-2 hover:bg-white/10 text-emerald-400 rounded-lg transition-all duration-200"
                              title="Resume"
                            >
                              <Play className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={stopFlyby}
                            className="p-2 hover:bg-red-500/20 text-gray-300 hover:text-red-300 rounded-lg transition-all duration-200"
                            title="Stop"
                          >
                            <Square className="w-3 h-3" />
                          </button>
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="flex items-center gap-2 flex-1">
                          <span className="text-xs text-gray-300 font-medium min-w-[32px]">{Math.round(flybyProgress)}%</span>
                          <div className="flex-1 h-1.5 bg-white/20 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all duration-300 shadow-sm"
                              style={{ width: `${flybyProgress}%` }}
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Second Row - Speed & Camera Controls */}
                      <div className="flex items-center gap-3">
                        {/* Speed Controls */}
                        <div className="flex items-center bg-white/5 backdrop-blur-sm rounded-lg px-1 py-1.5 border border-white/10">
                          <span className="text-xs text-gray-400 px-2">Speed</span>
                          <button
                            onClick={() => changeSpeed(45)}
                            className={`px-2 py-1 text-xs rounded transition-all duration-200 ${
                              flybySpeed === 45 ? 'bg-emerald-500/30 text-emerald-300' : 'text-gray-300 hover:bg-white/10'
                            }`}
                          >
                            0.5x
                          </button>
                          <button
                            onClick={() => changeSpeed(90)}
                            className={`px-2 py-1 text-xs rounded transition-all duration-200 ${
                              flybySpeed === 90 ? 'bg-emerald-500/30 text-emerald-300' : 'text-gray-300 hover:bg-white/10'
                            }`}
                          >
                            1x
                          </button>
                          <button
                            onClick={() => changeSpeed(135)}
                            className={`px-2 py-1 text-xs rounded transition-all duration-200 ${
                              flybySpeed === 135 ? 'bg-emerald-500/30 text-emerald-300' : 'text-gray-300 hover:bg-white/10'
                            }`}
                          >
                            1.5x
                          </button>
                        </div>
                        
                        {/* Camera Controls */}
                        <div className="flex items-center bg-white/5 backdrop-blur-sm rounded-lg px-1 py-1.5 border border-white/10">
                          <button
                            onMouseDown={() => startBearingAdjustment(-5)}
                            onMouseUp={stopBearingAdjustment}
                            onMouseLeave={stopBearingAdjustment}
                            onTouchStart={() => startBearingAdjustment(-5)}
                            onTouchEnd={stopBearingAdjustment}
                            className="p-1 hover:bg-white/20 text-gray-300 rounded transition-all duration-200"
                            title="Rotate Left"
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onMouseDown={() => startBearingAdjustment(5)}
                            onMouseUp={stopBearingAdjustment}
                            onMouseLeave={stopBearingAdjustment}
                            onTouchStart={() => startBearingAdjustment(5)}
                            onTouchEnd={stopBearingAdjustment}
                            className="p-1 hover:bg-white/20 text-gray-300 rounded transition-all duration-200"
                            title="Rotate Right"
                          >
                            <RotateCw className="w-3.5 h-3.5" />
                          </button>
                          <div className="w-px h-4 bg-white/20" />
                          <button
                            onMouseDown={() => startPitchAdjustment(-2)}
                            onMouseUp={stopPitchAdjustment}
                            onMouseLeave={stopPitchAdjustment}
                            onTouchStart={() => startPitchAdjustment(-2)}
                            onTouchEnd={stopPitchAdjustment}
                            className="p-1 hover:bg-white/20 text-gray-300 rounded transition-all duration-200"
                            title="Tilt Down"
                          >
                            <span className="text-xs">▼</span>
                          </button>
                          <button
                            onMouseDown={() => startPitchAdjustment(2)}
                            onMouseUp={stopPitchAdjustment}
                            onMouseLeave={stopPitchAdjustment}
                            onTouchStart={() => startPitchAdjustment(2)}
                            onTouchEnd={stopPitchAdjustment}
                            className="p-1 hover:bg-white/20 text-gray-300 rounded transition-all duration-200"
                            title="Tilt Up"
                          >
                            <span className="text-xs">▲</span>
                          </button>
                          <div className="w-px h-4 bg-white/20" />
                          <button
                            onMouseDown={() => startZoomAdjustment(-0.1)}
                            onMouseUp={stopZoomAdjustment}
                            onMouseLeave={stopZoomAdjustment}
                            onTouchStart={() => startZoomAdjustment(-0.1)}
                            onTouchEnd={stopZoomAdjustment}
                            className="p-1 hover:bg-white/20 text-gray-300 rounded transition-all duration-200"
                            title="Zoom Out"
                          >
                            <ZoomOut className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onMouseDown={() => startZoomAdjustment(0.1)}
                            onMouseUp={stopZoomAdjustment}
                            onMouseLeave={stopZoomAdjustment}
                            onTouchStart={() => startZoomAdjustment(0.1)}
                            onTouchEnd={stopZoomAdjustment}
                            className="p-1 hover:bg-white/20 text-gray-300 rounded transition-all duration-200"
                            title="Zoom In"
                          >
                            <ZoomIn className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
        </div>

        {/* Sidebar with Sliding Drawer - now overlaying the map */}
        <SidebarDrawer 
          items={sidebarItems} 
          activeDrawer={activeSidebarDrawer}
          onDrawerChange={setActiveSidebarDrawer}
          mode="preview"
          overlayMode={true}
        />
        
        {/* Elevation Profile Section - now overlaying the map */}
        <ElevationProfileSection
          showElevation={showElevation}
          selectedRouteId={selectedRouteId}
          getCurrentRoute={getCurrentRoute}
          routes={routes}
          pois={pois}
          routeInfo={routeInfo}
          navigateToRoute={navigateToRoute}
          getNavigableRoutes={getNavigableRoutes}
          handlePOISelect={handlePOISelect}
          setShowElevation={setShowElevation}
          onHoverDistanceChange={handleHoverDistanceChange}
          onClimbHover={handleClimbHover}
          onMinimizedChange={setElevationProfileMinimized}
          onActiveTabChange={setElevationActiveTab}
          routeId={routeId}
          // User POI props
          userPOIs={userPOISystem?.userPOIs || []}
          showUserPOIs={showUserPOIsInProfile}
          onToggleUserPOIs={() => setShowUserPOIsInProfile(!showUserPOIsInProfile)}
          onUserPOIClick={userPOISystem?.handlePOIClick}
          showResolved={userPOISystem?.showResolved}
          onToggleResolved={() => userPOISystem?.setShowResolved(!userPOISystem?.showResolved)}
          hasUserPOIs={!!userPOISystem}
          overlayMode={true}
          flybyProgress={flybyState !== 'stopped' ? flybyProgress : undefined}
        />
      </div>

      {/* Compact Modals */}
      <CompactPOIModal
        poi={selectedPOI}
        isOpen={showPOIModal}
        onClose={() => setShowPOIModal(false)}
        routes={routes}
      />

      <CompactPhotoModal
        photo={selectedPhoto}
        photos={sortedPhotos}
        isOpen={showPhotoModal}
        onClose={() => {
          setShowPhotoModal(false)
          // Clear photo highlight
          if (photoRenderer.current) {
            photoRenderer.current.highlightPhoto('')
          }
          // Reset map to normal view - just reset pitch, don't change zoom or bearing
          if (mapManager.current) {
            const map = mapManager.current.getMap()
            if (map) {
              map.easeTo({
                pitch: is3DEnabled ? 30 : 0,
                // Don't change bearing or zoom
                duration: 800
              })
            }
          }
        }}
        onNavigate={handlePhotoNavigate}
        routes={routes}
      />

      {/* User POI System Modals */}
      {userPOISystem.modals}

      {/* Route Embed Modal */}
      <RouteEmbedModal
        isOpen={showEmbedModal}
        onClose={() => setShowEmbedModal(false)}
        routeId={routeId || routes[0]?.id || ''}
        routeName={routeInfo.name}
      />
    </div>
  )
}